import Vue from 'vue';
import Component from 'vue-class-component';
import { Async } from '@/utils/AsyncDecorators';
import { Watch } from 'vue-property-decorator';
import { store } from '@/store';
import { versioneInSituStore } from '@/modules/VersioneInSitu/store/VersioneInSitu.store';
import { areaSaggioStore } from '@/modules/AreaSaggio/store/areaSaggio.store';
import { areaSaggioPiantaCampioneStore } from '@/modules/AreaSaggioPiantaCampione/store/areaSaggioPiantaCampione.store';
import { domainStore } from '@/modules/domains';
import Row from './row.vue'

import Highcharts from 'highcharts'
import exportingInit from 'highcharts/modules/exporting'
exportingInit(Highcharts)
import regressionInit from 'highcharts-regression'
import { nextTick } from 'vue/types/umd';
import { printerHelper } from '@/modules/printer';
import { nearest_unrolled } from '@/esriMap/src/customLayers/functions/upscalingFunctions';
import { CalculationUtils } from '../../utils/CalculationUtils';
import { reportStore } from '../../store/report.store';
regressionInit(Highcharts)

@Component({ components: { Row } })
export default class Index extends Vue {
  activeTab: string = "dati";
  hcInstance = Highcharts;

  @Async({ async get() { return await versioneInSituStore.getters.current() }, default: {}, lazy: false })
  currentVersion: server.VersioneInSitu;

  @Async({ async get() { return (await areaSaggioStore.getters.getAll()).sort((a, b) => a.nome.localeCompare(b.nome)); }, default: [], lazy: false })
  areeSaggio: server.areaSaggio[];

  @Async({ async get() { return await areaSaggioPiantaCampioneStore.getters.getAll() }, default: [], lazy: false })
  areaSaggioPiantaCampione: server.areaSaggioPiantaCampione[];

  @Async({ async get() { return await await domainStore.getters.specieArboree(); }, default: [], lazy: false })
  specieArboree: domains.domini[];

  @Watch('filter.text')
  change(n, o) { this.showsize = 20; }
  showsize: number = 20;

  get areeSaggioAutocomplete() {
    if (this.areeSaggio.length <= 0) return [];
    return this.areeSaggio.map((m) => {
      return {
        key: m.id,
        value: m.id,
        description: m.nome,
      } as server.autocomplete
    })
  }

  get areaSaggioId() {
    return store.getters.navigation.selected("areaSaggio");
  }
  get navigation() {
    if (!this.areaSaggioId) return null
    return this.areeSaggioAutocomplete.find(f => f.key === this.areaSaggioId) || {} as server.autocomplete;
  }
  set navigation(value: server.autocomplete) {
    if (value.key !== this.areaSaggioId) {
      store.actions.navigation.set_filter({ area: "areaSaggio", id: value ? value.key : null });
      reportStore.actions.set_logarithmic_regression(value ? value.key : null);
    }
  }

  get pianteCampione(): server.areaSaggioPiantaCampione[] {
    if (!this.areaSaggioId || this.areaSaggioPiantaCampione.length <= 0) return [];
    return this.areaSaggioPiantaCampione
      .filter(i => i.areaSaggioId === this.areaSaggioId)
      .sort((a, b) => {
        const specieOrder = a.specie - b.specie;
        if (specieOrder !== 0) return specieOrder;
        return a.diametro - b.diametro;
      });
  }

  get pianteCampioneResized(): server.areaSaggioPiantaCampione[] {
    return this.pianteCampione.slice(0, this.showsize);
  }

  get chartOptions() {
    if (this.pianteCampione.length <= 0) return null;
    return {
      chart: {
        type: 'scatter',
        zoomType: 'xy'
      },
      title: {
        text: `Rilievi ipsometrici area di saggio: <i>${this.areeSaggio.find(f => f.id === this.areaSaggioId).nome}</i>`
      },
      subtitle: {
        text: 'Curva ipsometrica'
      },
      xAxis: {
        title: {
          enabled: true,
          text: 'Diametro (cm)'
        },
        startOnTick: true,
        endOnTick: true,
        showLastLabel: true
      },
      yAxis: {
        title: {
          text: 'Altezza pianta (m)'
        }
      },
      legend: {
        layout: 'vertical',
        align: 'left',
        verticalAlign: 'top',
        x: 100,
        y: 70,
        floating: true,
        backgroundColor: '#FFFFFF',
        borderWidth: 1
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: {
              hover: {
                enabled: true,
                lineColor: 'rgb(100,100,100)'
              }
            }
          },
          states: {
            hover: {
              marker: {
                enabled: false
              }
            }
          },
          tooltip: {
            headerFormat: '<b>{point.point.name}</b> ({point.point.codice})<br>',
            pointFormat: 'Diametro: {point.x} cm<br>Altezza: {point.y} m'
          }
        }
      },
      series: [{
        regression: true,
        regressionSettings: {
          type: 'logarithmic',
          color: 'rgba(223, 83, 83, .9)',
          name: "%eq | r2: %r"
        },
        name: 'Rilievo ipsometrico',
        color: 'rgba(23, 83, 83, .5)',
        data: this.pianteCampione.map(m => {
          return {
            name: this.specieArboree.find(f => f.id === m.specie)?.label,
            codice: this.specieArboree.find(f => f.id === m.specie)?.value,
            x: (m.diametro || 0),
            y: (m.altezza || 0)
          }
        })
      }]
    }
  }

  @Watch("activeTab")
  activeTabChange() {
    const chartContainer = this.$refs.chartContainer as any;
    if (this.chartOptions && chartContainer && chartContainer.chart && chartContainer.chart.reflow) {
      this.$nextTick(() => {
        chartContainer.chart.reflow();
      });
    }
  }

  specie(item): string {
    if (item.specie && this.specieArboree && this.specieArboree.length != 0)
      return this.specieArboree.find(f => f.id === item.specie).value;
    return null;
  }

  index: number = 0;

  exportExcel() {
    var arrayToExport = this.pianteCampione.map(f => {
      return {
        "Progressivo pianta": this.index += 1,
        "Specie": this.specie(f),
        "Diametro": f.diametro,
        "Diametro (metà altezza)": f.diametroMetaAltezza,
        "Altezza": f.altezza
      }
    });
    printerHelper.printExcel("curva-ipsometrica", arrayToExport, "Curva Ipsometrica");
  }


  get formulaRegression() {
    return reportStore.getters.regression()?.string;
  }

}